import React, { useState, useEffect, useRef, useId } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  accountProtectionRequest,
  enrollAccountProtectionServiceRequest,
  parseTextWithHTML,
  resetAccountProtectionState,
  RootState,
  useRecaptchaToken,
} from '@rb-serve-app/core';
import Breadcrumbs from '../../components/BreadCrumbs';
import Accordion from '../../components/Accordion';
import useNavigationBlocker from '../../hooks/useNavigationBlocker';
import CommanModal from '../../components/CommanModal';
import Modal from 'react-modal';
import Spinner from '../../components/Spinner';
import { useSearchParams } from 'react-router-dom';

const AccountProtection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
 const [searchParams] = useSearchParams();
  const redirectedFrom = searchParams.get("redirectedFrom");

  const getRecaptchaToken = useRecaptchaToken();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountId, customerId } = useSelector(
    (state: RootState) => state.user
  );
  const {
    insurancedetailSuccess,
    insurancedetailError,
    loading,
    enrollAccountProtectionServiceDetails,
    enrollAccountProtectionServiceLoading,
    enrollAccountProtectionServiceError,
  } = useSelector((state: RootState) => state.accountProtection);
  const { entries, assets } = contentfulData || {};
  const accountProtectionService =
    entries?.rb_manage_card?.accountProtectionService || [];

  const isEnrolled =
    insurancedetailSuccess?.insuranceInformation?.enrolledInformation
      ?.insuranceIndicator === 'INSURED';
  const defaultAppropriatenessOption = accountProtectionService
    .find((item: any) => item.doesFitsforyou)
    ?.doesFitsforyou?.options?.find(
      (option: any) => String(option?.label || '').trim().toLowerCase() === 'no'
    );

  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);
  const [isDisclaimerCheckedAmIEligible, setIsDisclaimerCheckedAmIEligible] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isEnrollBtnClicked, setIsEnrollBtnClicked] = useState(false);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [selectedAppropriateness, setSelectedAppropriateness] =
    useState<any>(null);
  const accessibilityId = useId();
  const sectionPayloadData =
    accountProtectionService.find((item: any) => item.sections)?.sections || {};

  const [sectionsData, setSectionsData] = useState<any>([]);
  const hasAppropriatenessSelection = Boolean(
    selectedAppropriateness?.value
  );

  const normalizeRapRightForYou = (value: string | null) => {
    const normalizedValue = String(value || '').trim().toLowerCase();

    if (normalizedValue === 'yes' || normalizedValue === 'y') {
      return 'Y';
    }

    if (normalizedValue === 'no' || normalizedValue === 'n') {
      return 'N';
    }

    return 'Not Sure';
  };

  const getAppropriatenessSectionId = () => {
  const match = sectionPayloadData?.find((section: any) => {
    const title = String(section?.sectionTitle || '').toLowerCase();
    const body = String(section?.body || '').toLowerCase();
    return (
      title.includes('suitability') ||
      title.includes('appropriate') ||
      body.includes('appropriate')
    );
  });

  return match?.id || null;
};

const handleAppropriatenessChange = (option: any) => {
  const value = option?.value || '';
  setSelectedAppropriateness(option);

  const sectionId = getAppropriatenessSectionId() || 'RAP_SEC_2';

  if (isDisclaimerCheckedAmIEligible) {
    handleSectionData(sectionId, true, value);
  }
};

  const handleSectionData = (
    id: any,
    userSelected: boolean,
    rapRightForYouOption?: string | null
  ) => {
    const section = sectionPayloadData?.find(
      (item: any) => String(item.id) === String(id)
    );
    if (!section) {
      return;
    }
    const updatedSection = {
      ...section,
      userSelected,
      ...(String(id) === 'RAP_SEC_2' && {
        rapRightForYou: normalizeRapRightForYou(rapRightForYouOption ?? null),
      }),
    };
    setSectionsData((prev: any) => {
      const filteredData = prev.filter(
        (item: any) => String(item.id) !== String(id)
      );
      if (userSelected) {
        return [...filteredData, updatedSection];
      }
      return filteredData;
    });
  };
 

  const isSubmittingRef = useRef(false);

  const handleNavigationAttempt = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    }
  };

  const { unblockNavigation } = useNavigationBlocker({
    hasUnsavedChanges: hasChanges,
    onNavigationAttempt: handleNavigationAttempt,
    shouldBlock: true,
  });

  const toggleAccordion = (index: number) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (!accountProtectionService.length) return null;
  const headerSection = accountProtectionService[0];
  const enrolledSections = accountProtectionService
    .slice(1)
    .filter((item: any) => {
      const key = Object.keys(item)[0];

      return (
        key === 'coverage' || key === 'cancellation' || key === 'FAQsSection'
      );
    });

  const enrollmentSections = accountProtectionService
    .slice(1)
    .filter((item: any) => {
      const key = Object.keys(item)[0];

      return (
        key === 'coverage' ||
        key === 'whatDoesItCost' ||
        key === 'amIEligible' ||
        key === 'doesFitsforyou' ||
        key === 'additionalContentforCoverage'
      );
    });

  const sectionsToRender = isEnrolled ? enrolledSections : enrollmentSections;
  useEffect(() => {
    if (!isEnrolled && sectionsToRender.length) {
      setOpenIndexes(sectionsToRender.map((_: any, i: number) => i));
    }
    if (isEnrolled) {
      setOpenIndexes([]);
    }
    if (!isEnrolled) {
      setIsDisclaimerChecked(false);
      setIsDisclaimerCheckedAmIEligible(false);
      setSelectedAppropriateness(null);
    }
  }, [isEnrolled, sectionsToRender.length, defaultAppropriatenessOption]);


  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (
      accountId &&
      customerId &&
      !insurancedetailSuccess?.insuranceInformation?.enrolledInformation
        ?.insuranceIndicator
    ) {
      dispatch(accountProtectionRequest({ accountId, customerId }));
    }
  }, [accountId, customerId, dispatch]);

  useEffect(() => {
    if (
      isSubmittingRef.current &&
      enrollAccountProtectionServiceDetails &&
      !enrollAccountProtectionServiceLoading &&
      !enrollAccountProtectionServiceError
    ) {
      setIsEnrollBtnClicked(true);
      setShowErrorModal(false);
      isSubmittingRef.current = false;
    }
  }, [
    enrollAccountProtectionServiceDetails,
    enrollAccountProtectionServiceLoading,
    enrollAccountProtectionServiceError,
  ]);

  useEffect(() => {
    if (insurancedetailError || enrollAccountProtectionServiceError) {
      setIsEnrollBtnClicked(false);
      setShowErrorModal(true);
      isSubmittingRef.current = false;
    }
  }, [insurancedetailError, enrollAccountProtectionServiceError]);

  useEffect(() => {
    return () => {
      dispatch(resetAccountProtectionState());
    };
  }, []);

  useEffect(() => {
    if (!isEnrolled) {
      const hasFormChanges =
        isDisclaimerChecked ||
        isDisclaimerCheckedAmIEligible ||
        hasAppropriatenessSelection;
      setHasChanges(hasFormChanges);
    } else {
      setHasChanges(false);
    }
  }, [
    hasAppropriatenessSelection,
    isDisclaimerChecked,
    isDisclaimerCheckedAmIEligible,
    isEnrolled,
  ]);

  useEffect(() => {
    if (isEnrolled) {
      document.body.classList.add('hide-recaptcha-badge');
    } else {
      document.body.classList.remove('hide-recaptcha-badge');
    }
    // Cleanup on unmount to avoid side effects
    return () => {
      document.body.classList.remove('hide-recaptcha-badge');
    };
  }, [isEnrolled]);

  const ageRateIndicator =
    insurancedetailSuccess?.insuranceInformation?.enrolledInformation
      ?.ageRateIndicator;
  const sequenceNumber = ageRateIndicator?.replace('rate', '');
  const ageIndicator =
    insurancedetailSuccess?.insuranceInformation?.enrolledInformation
      ?.policyInformation?.[0]?.rateInformation?.sequenceNumber;
  const getCoverageContent = (coverage: any, seq?: string | number) => {
    if (!coverage) return coverage;
    const key = String(seq ?? '').padStart(2, '0');
    const variant = coverage?.variants?.[key];
    return variant ?? coverage;
  };

  const unsavedModalContent =
    accountProtectionService.find((item: any) => item.unSavedChangesModal)
      ?.unSavedChangesModal || {};

  const enrollmentSuccessContent =
    accountProtectionService.find((item: any) => item.enrollmentSuccessModal)
      ?.enrollmentSuccessModal || {};

  const errorModalContent = enrollAccountProtectionServiceError?.errorCode
    ? entries?.rb_manage_card?.reCaptchaErrRap[
        enrollAccountProtectionServiceError?.errorCode
      ]
    : accountProtectionService.find((item: any) => item.errorModal)?.errorModal;
  const version =
    accountProtectionService.find((item: any) => item.version)?.version || '';
  const category =
    accountProtectionService.find((item: any) => item.category)?.category || '';

  const legalDisclaimerContent =
    accountProtectionService.find((item: any) => item.rapLegalDisclaimer)
      ?.rapLegalDisclaimer || {};

  const toggleDisclaimer = () => {
    setDisclaimerExpanded(!disclaimerExpanded);
  };

  const renderAppropriatenessOptions = (content: any, index: number) => (
    <fieldset
      className="border-0 p-0 m-0"
      aria-describedby={
        content?.required
          ? `${accessibilityId}-section-${index}-required`
          : undefined
      }
    >
      <legend className="sr-only">
        {content?.description}
        {content?.required ? ' Required.' : ''}
      </legend>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-5">
        {content?.options?.map((option: any, idx: number) => (
          <label
            key={String(option?.value || idx)}
            className="inline-flex items-center gap-2 text-base text-gray-700 border border-gray-300 rounded px-4 py-3 bg-white"
          >
            <input
              type="radio"
              name="rap-appropriateness"
              value={option?.value}
              checked={
                Boolean(selectedAppropriateness?.value) &&
                selectedAppropriateness?.value === option?.value
              }
              required={content?.required}
              disabled={!isDisclaimerCheckedAmIEligible}
              onChange={() => handleAppropriatenessChange(option)}
              className={`h-4 w-4 accent-red-600 border-gray-400 focus:ring-2 focus:ring-gray-200 ${
                isDisclaimerCheckedAmIEligible
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              }`}
            />
            <span>{parseTextWithHTML(option?.label)}</span>
          </label>
        ))}
      </div>
      {hasAppropriatenessSelection && (
        <output className="mt-4 block text-base text-gray-700 leading-6">
          {parseTextWithHTML(
            content?.options?.find(
              (option: any) =>
                option?.value === selectedAppropriateness?.value
            )?.dynamicDescription || ''
          )}
        </output>
      )}
       <h5 className="mt-4 block text-base text-gray-700 leading-6">
          {parseTextWithHTML(
            content?.footerDescription || ''
          )}
        </h5>
    </fieldset>
  );

  return (
    <div className="bg-bg-body py-4 px-4 md:px-8">
      <Breadcrumbs
        items={[
          {
            name: redirectedFrom === 'offers' ? entries?.rb_offers?.rbViewallOffers?.offers?.headings?.pageTitle : entries?.rb_manage_card?.manageCardTitle,
            href: redirectedFrom === 'offers' ? '/offers' : '/manage-card',
          },
          { name: headerSection?.header, href: '/account-protection' },
        ]}
      />
      <div className="bg-white rounded-lg min-h-[70vh]">
        {loading ? (
          <div className="flex justify-center items-center h-[80vh]">
            <Spinner aria-label="Loading Enrollment" />
          </div>
        ) : (
          <>
            {' '}
            <div className="relative">
              <div className="md:block hidden">
                <img
                  src={assets?.accountProtectionDesktopImg}
                  alt="Account Protection"
                  className="w-full object-cover md:h-[380px] lg:h-[420px]"
                />
              </div>
              <div className="md:hidden block">
                <img
                  src={assets?.accountProtectionImg}
                  alt="Account Protection"
                  className="w-full object-cover h-[200px]"
                />
              </div>
              <div className="bg-white p-4 md:absolute md:top-12 md:left-12 md:max-w-[470px] md:shadow-md">
                <h2 className="md:text-2xl text-xl font-semibold">
                  {parseTextWithHTML(headerSection?.title)}
                </h2>
                <p className="text-gray-700 mt-1 font-medium">
                  {parseTextWithHTML(headerSection?.subtitle)}
                </p>
                <p className="mt-4 text-gray-700 leading-relaxed">
                  {parseTextWithHTML(headerSection?.description)}
                </p>
              </div>
            </div>
            <div className="md:p-6 p-4">
              {sectionsToRender.map((item: any, index: number) => {
                const key = Object.keys(item)[0];
                let content = item[key];
                if (!content) return null;
                if (key === 'additionalContentforCoverage' && !isEnrolled) {
                  let enrollButtonClassName =
                    'bg-gray-100 border border-gray-300 cursor-not-allowed text-gray-400';
                  if (
                    !enrollAccountProtectionServiceLoading &&
                    isDisclaimerChecked &&
                    isDisclaimerCheckedAmIEligible &&
                    hasAppropriatenessSelection
                  ) {
                    enrollButtonClassName =
                      'bg-red-700 hover:bg-[#b32218] text-white';
                  }

                  return (
                    <div key={key} className="mt-6">
                      <p className="mt-6 text-base text-gray-700 leading-6 px-4">
                        {parseTextWithHTML(content?.reviewText)}
                      </p>
                      <div className="mt-4 flex flex-col md:flex-row gap-3 md:gap-10 px-4">
                        {content?.documents
                          ?.slice(0, 2)
                          .map((doc: any, idx: number) => (
                            <div
                              key={idx}
                              className=" text-base font-semibold  inline-flex items-center gap-1"
                            >
                              {parseTextWithHTML(
                                doc.title,
                                'text-[#2F6FA7] hover:underline cursor-pointer'
                              )}
                              <img
                                src={assets?.icon_pdf}
                                alt="Document"
                                className="w-6 h-6"
                              />
                            </div>
                          ))}
                      </div>

                      <p className="mt-6 text-base text-gray-700 leading-6 px-4">
                        {parseTextWithHTML(content?.qubecresidentText)}
                      </p>

                      {content?.documents?.[2] && (
                        <div className="mt-3 px-4  text-base font-semibold   inline-flex items-center gap-1">
                          {parseTextWithHTML(
                            content.documents[2].title,
                            'text-[#2F6FA7] hover:underline cursor-pointer'
                          )}
                          <img
                            src={assets?.icon_pdf}
                            alt="Document"
                            className="w-6 h-6"
                          />
                        </div>
                      )}

                      <div className="mt-6">
                        <label className="flex items-start gap-3 border border-gray-200 p-4">
                          <input
                            id={`${accessibilityId}-terms-and-conditions`}
                            type="checkbox"
                            checked={isDisclaimerChecked}
                            disabled={!isDisclaimerCheckedAmIEligible}
                            aria-required="true"
                            aria-describedby={`${accessibilityId}-terms-and-conditions-required`}
                            onChange={(e) => {
                              setIsDisclaimerChecked((p) => !p);
                              handleSectionData('RAP_SEC_3', e.target.checked);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                if (!isDisclaimerCheckedAmIEligible) return;
                                e.preventDefault();
                                setIsDisclaimerChecked((p) => !p);

                                handleSectionData(
                                  'RAP_SEC_3',
                                  !isDisclaimerChecked
                                );
                              }
                            }}
                            className={`mt-0.5 h-5 w-5 shrink-0 accent-red-600 border-gray-400 focus:ring-2 focus:ring-gray-200 ${
                              isDisclaimerCheckedAmIEligible
                                ? 'cursor-pointer'
                                : 'cursor-not-allowed opacity-50'
                            }`}
                          />
                          <span
                            className={`text-base leading-6 text-gray-700 `}
                          >
                            {parseTextWithHTML(content?.disclaimer)}
                            <span
                              id={`${accessibilityId}-terms-and-conditions-required`}
                              className={`text-red-600 ml-1
                          `}
                              aria-label="Required"
                            >
                              *
                            </span>
                          </span>
                        </label>
                      </div>

                      <button
                        type="button"
                        disabled={
                        enrollAccountProtectionServiceLoading ||
                        !isDisclaimerChecked ||
                        !isDisclaimerCheckedAmIEligible ||
                        !hasAppropriatenessSelection
                        }
                        onClick={async () => {
                          if (!sequenceNumber) return;
                          isSubmittingRef.current = true;
                          const recaptchaToken =
                            await getRecaptchaToken('submit_rap');
                          const payload = {
                            sequenceNumber,
                            activate: 'true',
                            version: version,
                            category: category,
                            accountId,
                            customerId,
                            language:
                              localStorage.getItem('language') === 'fr'
                                ? 'fr'
                                : 'en_CA',
                            section: sectionsData,
                            enrollCtaValue: 'Accept',
                          };

                          const enrollRequestData = {
                            accountId,
                            customerId,
                            recaptchaToken,
                            payload,
                          };
                         

                          dispatch(
                            enrollAccountProtectionServiceRequest(enrollRequestData)
                          );
                          setHasChanges(false);
                        }}
                        className={`mt-8 w-[30%] md:w-[20%] lg:w-[10%] py-3 text-base font-semibold transition focus:ring-2 focus:ring-red-600 relative flex items-center justify-center ${enrollButtonClassName}`}
                          aria-busy={enrollAccountProtectionServiceLoading}
                      >
                        {enrollAccountProtectionServiceLoading ? (
                          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6"></div>
                        ) : (
                          content?.enrollBtn
                        )}
                      </button>
                    </div>
                  );
                }

                if (isEnrolled) {
                  if (key === 'coverage') {
                    content =
                      getCoverageContent(item[key], ageIndicator) || item[key];
                  }
                  if (key === 'FAQsSection' && isEnrolled) {
                    return (
                      <div key={key} className="mt-2 p-4 bg-gray-100">
                        <h3 className="text-lg font-semibold mb-2">
                          {parseTextWithHTML(content?.title)}
                        </h3>
                        <p className="text-base text-gray-700 leading-6 flex flex-wrap items-start gap-2">
                          {parseTextWithHTML(
                            content?.description,
                            'text-[#DA291C] hover:underline font-semibold break-all whitespace-normal'
                          )}
                          <span className="block sm:inline-block sm:self-center">
                            <img
                              src={assets?.open_in_browser_icon}
                              alt="linkIcon"
                              className="w-5 h-5"
                            />
                          </span>
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div key={key} className="bg-gray-100 p-4 mt-2">
                      <h3 className="text-lg font-semibold mb-2">
                        {parseTextWithHTML(content?.title)}
                      </h3>
                      <p className="text-base text-gray-700 leading-6">
                        {parseTextWithHTML(content?.description)}
                      </p>
                    </div>
                  );
                }
                if (key === 'doesFitsforyou') {
                  return (
                    <section
                      key={key}
                      className="px-4 pb-6 border-b border-gray-300"
                    >
                      <h3
                        className="m-0 mb-3"
                        style={{
                          color: '#000',
                          fontFamily:
                            'var(--Typography-Font-family, "Ted Next")',
                          fontSize: '18px',
                          fontStyle: 'normal',
                          fontWeight: 650,
                          lineHeight: '32px',
                          letterSpacing:
                            'var(--Body-Regular-Letter-spacing, 0)',
                        }}
                      >
                        {parseTextWithHTML(content?.title)}
                      </h3>
                      <p
                        id={`${accessibilityId}-section-${index}-question`}
                        className="mb-3 text-base text-gray-700 leading-6"
                      >
                        {parseTextWithHTML(content?.description)}
                        {content?.required && (
                          <span
                            id={`${accessibilityId}-section-${index}-required`}
                            className="text-red-600 ml-1"
                            aria-label="Required"
                          >
                            *
                          </span>
                        )}
                      </p>
                      {renderAppropriatenessOptions(content, index)}
                    </section>
                  );
                }
                return (
                  <React.Fragment key={key}>
                  <Accordion
                    title={parseTextWithHTML(content?.title)}
                    isOpen={openIndexes.includes(index)}
                    onClick={() => toggleAccordion(index)}
                    extrastyling={true}
                  >
                    <div  className="transition-all duration-300 px-4 pb-6">
                    <p
                      id={`${accessibilityId}-section-${index}-question`}
                      className="mb-3 text-base text-gray-700 leading-6"
                    >
                      {parseTextWithHTML(content?.description)}
                      {key === 'doesFitsforyou' && content?.required && (
                        <span
                          id={`${accessibilityId}-section-${index}-required`}
                          className="text-red-600 ml-1"
                          aria-label="Required"
                        >
                          *
                        </span>
                      )}
                    </p>
 </div>
                  </Accordion>
                  <div className={`${
                    content?.disclaimer && content?.type === "checkbox" ? "border-b border-gray-300 mb-4" : ""
                  }`}>
                     {content?.disclaimer && content?.type === "checkbox" && (
                       <div className="mt-6 border-b border-gray-[#E9E9E9] mb-2">
                        <label className="flex items-start gap-3 border border-gray-200 p-4">
                          <input
                            id={`${accessibilityId}-section-${index}-checkbox`}
                            type="checkbox"
                            checked={isDisclaimerCheckedAmIEligible}
                            aria-required="true"
                            aria-describedby={`${accessibilityId}-section-${index}-question ${accessibilityId}-section-${index}-required`}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsDisclaimerCheckedAmIEligible(checked);
                              if (!checked) {
                                setIsDisclaimerChecked(false);
                                setSelectedAppropriateness(null);
                                handleSectionData('RAP_SEC_3', false);
                              }
                              handleSectionData(
                                'RAP_SEC_2',
                                checked,
                                checked ? selectedAppropriateness?.value : null
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const checked = !isDisclaimerCheckedAmIEligible;
                                setIsDisclaimerCheckedAmIEligible(checked);
                                if (!checked) {
                                  setIsDisclaimerChecked(false);
                                  setSelectedAppropriateness(null);
                                  handleSectionData('RAP_SEC_3', false);
                                }
                                handleSectionData(
                                  'RAP_SEC_2',
                                  checked,
                                  checked
                                    ? selectedAppropriateness?.value
                                    : null
                                );
                              }
                            }}
                            className={`mt-0.5 h-5 w-5 shrink-0 accent-red-600 border-gray-400 focus:ring-2 focus:ring-gray-200 cursor-pointer`}
                          />
                          <span
                            className={`text-base leading-6 text-gray-700 `}
                          >
                            {parseTextWithHTML(content?.disclaimer)}
                            <span
                              id={`${accessibilityId}-section-${index}-checkbox-required`}
                              className={`text-red-600 ml-1
                          `}
                              aria-label="Required"
                            >
                              *
                            </span>
                          </span>
                        </label>
                      </div>
                     )}
                    {renderAppropriatenessOptions(content, index)}
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="pl-6 pb-6">
              <button
                className="text-[#DA291C] text-left hover:underline focus:outline-none focus:ring-2 focus:ring-[#DA291C] focus:ring-opacity-50 transition-colors duration-200"
                onClick={toggleDisclaimer}
                aria-label={legalDisclaimerContent?.buttonText}
              >
                {disclaimerExpanded ? '[-] ' : '[+] '}
                {legalDisclaimerContent?.buttonText || '[+] Legal disclaimer'}
              </button>

              {disclaimerExpanded && (
                <div className="mt-2 p-4">
                  <p className="text-base text-gray-700 leading-6">
                    {isEnrolled
                      ? parseTextWithHTML(
                          legalDisclaimerContent?.contentForAlreadyEnrolled,
                          'text-[#2F6FA7] hover:underline cursor-pointer'
                        )
                      : parseTextWithHTML(
                          legalDisclaimerContent?.contentForEnrollment,
                          'text-[#2F6FA7] hover:underline cursor-pointer'
                        )}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {(showUnsavedChangesModal || showErrorModal) && (
        <CommanModal
          isOpen={showUnsavedChangesModal || showErrorModal}
          jsxContent={
            <>
              <div
                className="mt-[20px] text-lg font-semibold text-center"
                aria-label={
                  showUnsavedChangesModal
                    ? unsavedModalContent?.title
                    : errorModalContent?.title
                }
              >
                {showUnsavedChangesModal
                  ? parseTextWithHTML(unsavedModalContent?.title)
                  : parseTextWithHTML(errorModalContent?.title)}
              </div>
              <div
                className="mt-[20px] text-center"
                aria-label={
                  showUnsavedChangesModal
                    ? unsavedModalContent?.description
                    : errorModalContent?.description
                }
              >
                {showUnsavedChangesModal
                  ? parseTextWithHTML(unsavedModalContent?.description)
                  : parseTextWithHTML(errorModalContent?.description)}
              </div>
            </>
          }
          onClose={() => {
            if (showUnsavedChangesModal) {
              setShowUnsavedChangesModal(false);
            } else if (showErrorModal) {
              dispatch(resetAccountProtectionState());
              setShowErrorModal(false);
              navigate('/manage-card');
            }
          }}
          buttonText={
            showUnsavedChangesModal
              ? unsavedModalContent?.cancelCta
              : entries?.rb_manage_card?.reCaptchaErrRap?.buttonText
          }
          extraButtonText={
            showUnsavedChangesModal && unsavedModalContent?.continueCta
          }
          cardStatus={true}
          onExtraButtonClick={() => {
            if (showUnsavedChangesModal) {
              setShowUnsavedChangesModal(false);
              setHasChanges(false);
              unblockNavigation();
            }
          }}
          closeIcon={false}
        />
      )}

      {isEnrollBtnClicked && (
        <Modal
          isOpen={isEnrollBtnClicked}
          contentLabel="Enrollment confirmation modal"
          className="fixed p-0 w-[420px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[24px] rounded-lg bg-white relative">
            <img
              src={assets?.icon_success}
              alt="successIcon"
              className="w-20 h-20"
            />
            <h3 className="text-[#1F1F1F] text-[18px] font-[600] text-center">
              {parseTextWithHTML(enrollmentSuccessContent?.title)}
            </h3>
            <p className="text-[#1F1F1F] text-[16px] text-center">
              {parseTextWithHTML(enrollmentSuccessContent?.description)}
            </p>
            <button
              type="button"
              onClick={() => {
                dispatch(resetAccountProtectionState());
                setIsEnrollBtnClicked(false);
                navigate('/manage-card');
              }}
              className="mt-4 w-full py-[12px] border border-[#DA291C] text-white bg-primary-red font-semibold"
            >
              {enrollmentSuccessContent?.buttonText}
            </button>
          </div>
        </Modal>
      )}

  
    </div>
  );
};

export default AccountProtection;
